import React from 'react';
import { Play, Pause, Square, SkipBack, Clock } from 'lucide-react';
import { useScope } from '../../../contexts/DemoScopeContext';

const PlaybackControls = ({ disabled }) => {
  const {
    analysisEnabled,
    playbackState,
    startPlayback, pausePlayback, stopPlayback, seekPlayback, setPlaybackSpeed,
  } = useScope();

  const isDisabled = typeof disabled === 'boolean' ? disabled : !analysisEnabled;
  const { isPlaying, currentTime, duration, speed } = playbackState;

  // Format time as MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className={`playback-controls ${isDisabled ? 'disabled' : ''}`}>
      {/* Time display */}
      <div className="playback-time-display">
        <span className="playback-current-time">{formatTime(currentTime)}</span>
        <span className="playback-time-separator">/</span>
        <span className="playback-duration">{formatTime(duration)}</span>
      </div>

      {/* Scrubber */}
      <div className="playback-scrubber-container">
        <input
          type="range"
          className="playback-scrubber"
          min={0}
          max={duration || 0}
          step={0.01}
          value={Math.min(currentTime, duration || 0)}
          onChange={(e) => seekPlayback(parseFloat(e.target.value))}
          disabled={isDisabled || duration <= 0}
          aria-label="Playback position"
          title="Playback position"
        />
        <div
          className="playback-scrubber-fill"
          style={{ width: duration > 0 ? `${(currentTime / duration) * 100}%` : '0%' }}
        />
      </div>

      {/* Controls row */}
      <div className="playback-buttons-row">
        <div className="playback-transport">
          {/* Rewind to start */}
          <button
            type="button"
            className="playback-btn"
            disabled={isDisabled || duration <= 0}
            onClick={() => seekPlayback(0)}
            aria-label="Rewind to start"
            title="Rewind to start"
          >
            <SkipBack size={14} />
          </button>

          {/* Play / Pause */}
          <button
            type="button"
            className={`playback-btn playback-btn-primary ${isPlaying ? 'playing' : ''}`}
            disabled={isDisabled || duration <= 0}
            onClick={() => (isPlaying ? pausePlayback() : startPlayback())}
            aria-label={isPlaying ? 'Pause' : 'Play'}
            title={isPlaying ? 'Pause' : 'Play'}
          >
            {isPlaying ? <Pause size={14} /> : <Play size={14} />}
          </button>

          {/* Stop */}
          <button
            type="button"
            className="playback-btn"
            disabled={isDisabled || duration <= 0}
            onClick={() => stopPlayback()}
            aria-label="Stop"
            title="Stop"
          >
            <Square size={12} />
          </button>
        </div>

        {/* Speed control */}
        <div className="playback-speed-control">
          <Clock size={12} className="playback-speed-icon" />
          <select
            value={speed}
            onChange={(e) => setPlaybackSpeed(parseFloat(e.target.value))}
            disabled={isDisabled || duration <= 0}
            className="playback-speed-select"
            aria-label="Playback speed"
          >
            <option value={0.25}>0.25×</option>
            <option value={0.5}>0.5×</option>
            <option value={1}>1×</option>
            <option value={2}>2×</option>
            <option value={4}>4×</option>
          </select>
        </div>
      </div>
    </div>
  );
};

export default PlaybackControls;
